/**
 * The CRM's own confirm and question windows, instead of the browser's confirm() / prompt(): they look like the rest of
 * the CRM (light and dark), keep keyboard focus inside, close with Escape, and work in the installed phone app.
 * `confirmDialog` resolves true / false; `promptDialog` resolves the text typed, or null when cancelled.
 */
export interface DialogRequest {
  kind: 'confirm' | 'prompt';
  message: string;
  defaultValue: string;
  resolve: (value: boolean | string | null) => void;
}

let current: DialogRequest | null = null;
const listeners = new Set<() => void>();
const queue: DialogRequest[] = [];

function show(req: DialogRequest) {
  if (current) { queue.push(req); return; }
  current = req;
  listeners.forEach((l) => l());
}

/** Called by the window when it closes: answers the request and shows the next one waiting, if any. */
export function answerDialog(value: boolean | string | null) {
  const req = current;
  current = null;
  req?.resolve(value);
  const next = queue.shift();
  if (next) show(next); else listeners.forEach((l) => l());
}

export const subscribeDialog = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const currentDialog = () => current;

export function confirmDialog(message: string): Promise<boolean> {
  return new Promise((resolve) => show({ kind: 'confirm', message, defaultValue: '', resolve: (v) => resolve(v === true) }));
}

export function promptDialog(message: string, defaultValue = ''): Promise<string | null> {
  return new Promise((resolve) => show({ kind: 'prompt', message, defaultValue, resolve: (v) => resolve(typeof v === 'string' ? v : null) }));
}
